import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './lib/utils';

const inputVariants = cva(
  'flex w-full rounded-md border border-input bg-background px-3  text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        default: 'h-10',
        sm: 'h-8 px-2',
        lg: 'h-12 px-4',
      },
      variant: {
        default: 'focus-visible:ring-primary',
        outline: 'border-2',
        ghost: 'border-none',
      },
    },
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
  }
);

export interface InputProps
  extends Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      'size' | 'prefix' | 'suffix'
    >,
    VariantProps<typeof inputVariants> {
  label?: string;
  suffix?: React.ReactNode;
  prefix?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, size, variant, label, suffix, prefix, ...props },
    ref
  ) => {
    return (
      <div className="relative">
        <label
          className={cn(
            'absolute left-3 pointer-events-none transform -translate-y-1.5 text-xs text-secondary bg-background ml-4 px-1'
          )}
        >
          {label}
        </label>
        {prefix && (
          <div className="absolute left-1 top-1/2 transform -translate-y-1/2 pointer-events-none text-sm">
            {prefix}
          </div>
        )}
        <input
          type={type}
          className={cn(
            inputVariants({ size, variant }),
            prefix && 'pl-8',
            suffix && 'pr-8',
            className
          )}
          ref={ref}
          {...props}
        />
        {suffix && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-sm">
            {suffix}
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
